import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { SIRUC_INSPECTIONS } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { feedback } from "@/lib/feedback";
import { Link } from "react-router-dom";
import { Plus, ClipboardCheck, Check, Trash2, FileText, X, ShieldCheck } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

export default function SirucInspections() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [approvingId, setApprovingId] = useState(null);
  const [approvalForm, setApprovalForm] = useState({ firma_supervisor: "", comentarios: "" });

  const canApprove = ["admin", "supervisor"].includes(user?.role);
  const isAdmin = user?.role === "admin";

  const { data: inspections = [], isLoading } = useQuery({
    queryKey: ["siruc-inspections"],
    queryFn: () => api.get("/siruc/inspections").then((r) => r.data),
  });

  const approveMutation = useMutation({
    mutationFn: (id) => api.post(`/siruc/inspections/${id}/approve`, approvalForm).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["siruc-inspections"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Inspección aprobada correctamente");
      feedback.success();
      setApprovingId(null);
      setApprovalForm({ firma_supervisor: "", comentarios: "" });
    },
    onError: (e) => { toast.error(apiError(e, "Error al aprobar")); feedback.error(); },
  });

  const handleApproveClick = (id) => {
    setApprovingId(id);
    setApprovalForm({ firma_supervisor: "", comentarios: "" });
  };

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/siruc/inspections/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["siruc-inspections"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Inspección eliminada");
    },
    onError: (e) => toast.error(apiError(e, "Error al eliminar")),
  });

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight text-navy">Inspecciones C-TPAT</h1>
          <p className="text-sm text-muted-foreground mt-1">Inspección de 19 y 9 puntos.</p>
        </div>
        <Link
          to="/siruc/inspections/new"
          data-testid={SIRUC_INSPECTIONS.newButton}
          className="flex items-center gap-2 h-11 px-4 bg-navy text-white font-semibold hover:bg-navy-hover"
        >
          <Plus size={18} /> Nueva inspección
        </Link>
      </header>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : inspections.length === 0 ? (
        <div className="bg-white border border-border p-12 flex flex-col items-center text-center">
          <span className="w-14 h-14 bg-secondary text-navy flex items-center justify-center mb-4">
            <ClipboardCheck size={28} />
          </span>
          <h3 className="font-heading text-xl font-bold text-navy">Sin inspecciones</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-sm">Crea la primera inspección C-TPAT.</p>
          <Link
            to="/siruc/inspections/new"
            data-testid={SIRUC_INSPECTIONS.newButton}
            className="h-11 px-4 bg-navy text-white font-semibold hover:bg-navy-hover"
          >
            + Nueva inspección
          </Link>
        </div>
      ) : (
        <div className="bg-white border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-secondary border-b border-border">
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Tipo</th>
                  <th className="px-4 py-3 font-medium">Placas</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Trailer</th>
                  <th className="px-4 py-3 font-medium hidden lg:table-cell">Inspector</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Aprobación</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">Fecha</th>
                  {canApprove && <th className="px-4 py-3"></th>}
                </tr>
              </thead>
              <tbody data-testid={SIRUC_INSPECTIONS.list} className="divide-y divide-border">
                {inspections.map((i) => (
                  <tr key={i.id} data-testid={SIRUC_INSPECTIONS.row} className="hover:bg-secondary/50">
                    <td className="px-4 py-3 font-medium text-navy">
                      {i.inspection_type === "9_puntos" ? "9 puntos" : "19 puntos"}
                    </td>
                    <td className="px-4 py-3">{i.placas_unidad || "—"}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{i.numero_trailer || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-muted-foreground">{i.inspector_nombre || i.created_by || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 ${
                        i.status_general === "bueno" ? "bg-success/10 text-success" : "bg-critical/10 text-critical"
                      }`}>
                        {i.status_general === "bueno" ? "Bueno" : "Malo"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 ${
                        i.approval_status === "aprobada" ? "bg-success/10 text-success" : "bg-warn/10 text-warn"
                      }`}>
                        {i.approval_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-muted-foreground text-xs">
                      {new Date(i.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
                    </td>
                    {canApprove && (
                      <td className="px-4 py-3 text-right flex items-center justify-end gap-2">
                        <Link
                          to={`/siruc/inspections/edit/${i.id}`}
                          className="p-1.5 text-navy hover:bg-navy/10 rounded"
                        >
                          <FileText size={16} />
                        </Link>
                        {i.approval_status !== "aprobada" && (
                          <button
                            onClick={() => handleApproveClick(i.id)}
                            data-testid={SIRUC_INSPECTIONS.approveButton}
                            className="inline-flex items-center gap-1.5 h-8 px-3 border border-success text-success text-xs font-medium hover:bg-success hover:text-white transition-colors"
                          >
                            <Check size={14} /> Aprobar
                          </button>
                        )}
                        {isAdmin && (
                          <button
                            onClick={() => {
                              if (confirm("¿Eliminar esta inspección?")) deleteMutation.mutate(i.id);
                            }}
                            className="p-1.5 text-critical hover:bg-critical/10 rounded"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de Aprobación por Supervisor */}
      {approvingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white border-2 border-navy w-full max-w-md overflow-hidden shadow-2xl rounded-xl">
            <div className="bg-navy p-4 flex items-center justify-between text-white">
              <div className="flex items-center gap-2">
                <ShieldCheck size={20} />
                <h3 className="font-heading font-bold uppercase tracking-tight">Aprobar Inspección</h3>
              </div>
              <button onClick={() => setApprovingId(null)} className="hover:bg-white/10 p-1 rounded transition-colors">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black uppercase text-navy/60 tracking-widest">Comentarios (Opcional)</label>
                <textarea
                  value={approvalForm.comentarios}
                  onChange={(e) => setApprovalForm({ ...approvalForm, comentarios: e.target.value })}
                  placeholder="Observaciones de la aprobación..."
                  className="input min-h-[80px] bg-secondary/20 border-none text-sm"
                />
              </div>
              <div className="space-y-4">
                <SignaturePad
                  label="FIRMA DEL SUPERVISOR *"
                  value={approvalForm.firma_supervisor}
                  onChange={(v) => setApprovalForm({ ...approvalForm, firma_supervisor: v })}
                  testid="siruc-firma-supervisor-approval"
                />
              </div>
              <button
                disabled={!approvalForm.firma_supervisor || approveMutation.isPending}
                onClick={() => approveMutation.mutate(approvingId)}
                className="w-full h-14 bg-success text-white font-black uppercase tracking-widest shadow-lg active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {approveMutation.isPending ? "Procesando..." : <><Check size={20} /> Confirmar Aprobación</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
